import BlockIcon from "@mui/icons-material/Block";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Badge,
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useAsync } from "react-use";
import { useApi } from "../../../containers/ApiContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { Aircraft } from "../../../types";
import { Loading } from "../../../widgets/Loading";
import {
  AircraftSuggestion,
  getSuggestedAircraft,
} from "./aircraftSuggestionUtils";
import { getCountryFlag } from "./countryFlags";
import { useFAAMappingSuggestions } from "./hooks/useFAAMappingSuggestions";
import { SuggestedAircraftCell } from "./SuggestedAircraftCell";
import { RecentPatternsPanel, FAAMappingPattern } from "./RecentPatternsPanel";
import { useIdentity } from "../../../containers/IdentityContainer";

type Props = {};

type CountryOption = {
  country: string;
  prefix: string;
};

export const AircraftMappingTab: React.FC<Props> = () => {
  const [query, setQuery] = React.useState<string>("");
  const [showOnlyUnmatched, setShowOnlyUnmatched] =
    React.useState<boolean>(false);
  const [showOnlyWithSuggestions, setShowOnlyWithSuggestions] =
    React.useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = React.useState<CountryOption>({
    country: "All Countries",
    prefix: "",
  });
  const [selectedManufacturer, setSelectedManufacturer] =
    React.useState<string>("All Manufacturers");
  const [approvedMappings, setApprovedMappings] = React.useState<
    Map<string, Aircraft>
  >(new Map());
  const [blacklistedRows, setBlacklistedRows] = React.useState<Set<string>>(
    new Set()
  );

  // Bulk apply state
  const [bulkApplyAircraft, setBulkApplyAircraft] = React.useState<Aircraft | null>(null);
  const [bulkApplyRowKey, setBulkApplyRowKey] = React.useState<string | null>(null);
  const [bulkApplying, setBulkApplying] = React.useState(false);

  const { postgrest } = useApi();
  const snackbar = useSnackbar();
  const { airplxIdentity } = useIdentity();

  // Debounce timer for search input
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debounced search handler (75ms delay)
  const handleSearchChange = React.useCallback((value: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      // Normalize whitespace: trim and collapse multiple spaces to single space
      const normalized = value.replace(/\s+/g, " ").trim();
      setQuery(normalized);
    }, 75);
  }, []);

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Persistent cache for suggestion calculations (survives across filter changes)
  const suggestionCacheRef = React.useRef<
    Map<string, AircraftSuggestion>
  >(new Map());

  // Fetch aircraft with historical FAA mappings for intelligent suggestions
  const {
    aircraftWithHistory,
    csvMatcher,
    loading: loadingAircraft,
  } = useFAAMappingSuggestions();

  // Clear cache when aircraft data or matcher changes (ensures fresh suggestions)
  React.useEffect(() => {
    suggestionCacheRef.current.clear();
  }, [aircraftWithHistory, csvMatcher]);

  // Fetch list of countries from registration prefixes
  const { value: countries } = useAsync(async () => {
    const { data } = await postgrest
      .from("aircraft_registration_prefixes")
      .select("country, prefix")
      .order("country");

    if (!data) return [];

    // Deduplicate by country (some countries have multiple prefixes)
    const uniqueCountries = Array.from(
      new Map(data.map((item) => [item.country, item])).values()
    );

    return uniqueCountries as CountryOption[];
  }, [postgrest]);

  const [rowsRefreshKey, setRowsRefreshKey] = React.useState(0);
  const { loading, value: rows } = useAsync(async () => {
    const { data } = await postgrest.from("vw_faa_mapping").select();
    return data || [];
  }, [postgrest, rowsRefreshKey]);

  // Fetch saved patterns
  const [patternsRefreshKey, setPatternsRefreshKey] = React.useState(0);
  const { loading: loadingPatterns, value: patterns = [] } = useAsync(async () => {
    const { data } = await postgrest
      .from("faa_mapping_patterns")
      .select("*, aircraft:matched_aircraft_id(id, make, model)")
      .order("created_at", { ascending: false })
      .limit(10);

    return (data || []) as FAAMappingPattern[];
  }, [postgrest, patternsRefreshKey]);

  // Helper function to format frequency display
  const formatFrequency = (
    row: any,
    selectedCountry: CountryOption
  ): string => {
    const overall = row.freq || 0;

    // When "All Countries" is selected, just show overall
    if (selectedCountry.country === "All Countries") {
      return overall.toString();
    }

    // When country is selected, show: "countryCount (overall)"
    const countryCounts = row.country_counts as Record<string, number> | null;
    const countrySpecific = countryCounts?.[selectedCountry.country] || 0;

    return `${countrySpecific} (${overall})`;
  };

  const rowsToDisplay = React.useMemo(() => {
    if (!rows) {
      return [];
    }

    let filtered = rows.filter((row) => {
      // Manufacturer filter
      if (selectedManufacturer !== "All Manufacturers") {
        if (row.faa_manufacturer !== selectedManufacturer) {
          return false;
        }
      }

      // Country filter
      if (selectedCountry.country !== "All Countries") {
        // Check if selected country is in the countries array
        const rowCountries = row.countries as string[] | null;
        if (!rowCountries || !rowCountries.includes(selectedCountry.country)) {
          return false;
        }
      }

      // Text search filter (client-side, combined search across manufacturer + model)
      if (query.trim() !== "") {
        const searchLower = query.toLowerCase();
        const manufacturerLower = row.faa_manufacturer.toLowerCase();
        const modelLower = row.faa_model.toLowerCase();
        const combined = `${manufacturerLower} ${modelLower}`;

        // Match if query appears in combined text (handles "Gulfstream Aerospace" matching both fields)
        const matchesCombined = combined.includes(searchLower);

        if (!matchesCombined) {
          return false;
        }
      }

      // Unmatched filter
      if (showOnlyUnmatched && row.aircraft_id) {
        return false;
      }

      return true;
    });

    // Sort by country-specific count when country filter is active
    if (selectedCountry.country !== "All Countries") {
      filtered = [...filtered].sort((a, b) => {
        const aCount =
          (a.country_counts as Record<string, number> | null)?.[
            selectedCountry.country
          ] || 0;
        const bCount =
          (b.country_counts as Record<string, number> | null)?.[
            selectedCountry.country
          ] || 0;
        return bCount - aCount; // Descending - highest country-specific count first
      });
    }
    // else: keep default sort from view (by overall freq DESC)

    // Filter out locally blacklisted rows (optimistic UI)
    filtered = filtered.filter((row) => {
      const key = `${row.faa_manufacturer}|${row.faa_model}`;
      return !blacklistedRows.has(key);
    });

    return filtered.slice(0, 100);
  }, [
    rows,
    query,
    showOnlyUnmatched,
    selectedCountry,
    selectedManufacturer,
    blacklistedRows,
  ]);

  // Calculate suggestions for visible rows using persistent cache
  // Only calculates new suggestions that aren't already cached
  const suggestions = React.useMemo(() => {
    if (!aircraftWithHistory || !rowsToDisplay) {
      return new Map<string, AircraftSuggestion>();
    }

    const cache = suggestionCacheRef.current;
    const map = new Map<string, AircraftSuggestion>();

    rowsToDisplay.forEach((row) => {
      const key = `${row.faa_manufacturer}|${row.faa_model}`;

      // Check cache first
      if (cache.has(key)) {
        map.set(key, cache.get(key)!);
      } else {
        // Calculate and cache new suggestion
        const suggestion = getSuggestedAircraft(
          row.faa_manufacturer,
          row.faa_model,
          aircraftWithHistory,
          csvMatcher
        );
        cache.set(key, suggestion);
        map.set(key, suggestion);
      }
    });

    return map;
  }, [rowsToDisplay, aircraftWithHistory, csvMatcher]);

  // Apply suggestions filter if enabled (must happen after suggestions are calculated)
  const filteredRowsToDisplay = React.useMemo(() => {
    if (!showOnlyWithSuggestions) {
      return rowsToDisplay;
    }

    return rowsToDisplay.filter((row) => {
      const key = `${row.faa_manufacturer}|${row.faa_model}`;
      const suggestion = suggestions.get(key);
      // Only show rows that have a valid suggestion (confidence is not null)
      return suggestion?.confidence !== null;
    });
  }, [rowsToDisplay, suggestions, showOnlyWithSuggestions]);

  // Count of suggestions available (excluding already mapped/approved)
  const suggestionCount = React.useMemo(() => {
    let count = 0;
    filteredRowsToDisplay.forEach((row) => {
      const key = `${row.faa_manufacturer}|${row.faa_model}`;
      const suggestion = suggestions.get(key);
      const hasExistingMapping = row.aircraft_id || approvedMappings.has(key);

      if (suggestion?.confidence !== null && !hasExistingMapping) {
        count++;
      }
    });
    return count;
  }, [suggestions, filteredRowsToDisplay, approvedMappings]);

  // Calculate confidence level breakdown for suggestions
  const confidenceBreakdown = React.useMemo(() => {
    let high = 0;
    let medium = 0;

    filteredRowsToDisplay.forEach((row) => {
      const key = `${row.faa_manufacturer}|${row.faa_model}`;
      const suggestion = suggestions.get(key);
      const hasExistingMapping = row.aircraft_id || approvedMappings.has(key);

      if (!hasExistingMapping && suggestion?.confidence) {
        if (suggestion.confidence === "high") {
          high++;
        } else if (suggestion.confidence === "medium") {
          medium++;
        }
      }
    });

    return { high, medium };
  }, [suggestions, filteredRowsToDisplay, approvedMappings]);

  // Calculate tail number coverage based on current filters
  const coverageStats = React.useMemo(() => {
    if (!rows) return null;

    // Apply same filters as rowsToDisplay (without 100-row limit)
    const filtered = rows.filter((row) => {
      // Manufacturer filter
      if (selectedManufacturer !== "All Manufacturers") {
        if (row.faa_manufacturer !== selectedManufacturer) {
          return false;
        }
      }

      // Country filter
      if (selectedCountry.country !== "All Countries") {
        const rowCountries = row.countries as string[] | null;
        if (!rowCountries || !rowCountries.includes(selectedCountry.country)) {
          return false;
        }
      }

      // Text search filter (client-side, combined search across manufacturer + model)
      if (query.trim() !== "") {
        const searchLower = query.toLowerCase();
        const manufacturerLower = row.faa_manufacturer.toLowerCase();
        const modelLower = row.faa_model.toLowerCase();
        const combined = `${manufacturerLower} ${modelLower}`;

        // Match if query appears in combined text (handles "Gulfstream Aerospace" matching both fields)
        const matchesCombined = combined.includes(searchLower);

        if (!matchesCombined) {
          return false;
        }
      }

      // Unmatched filter
      if (showOnlyUnmatched && row.aircraft_id) {
        return false;
      }

      // Exclude blacklisted rows from statistics
      const key = `${row.faa_manufacturer}|${row.faa_model}`;
      if (blacklistedRows.has(key)) {
        return false;
      }

      return true;
    });

    const totalTails = filtered.reduce((sum, r) => sum + (r.freq || 0), 0);
    const mappedTails = filtered
      .filter((r) => {
        const key = `${r.faa_manufacturer}|${r.faa_model}`;
        // Count BOTH database mappings AND local approved mappings
        return r.aircraft_id || approvedMappings.has(key);
      })
      .reduce((sum, r) => sum + (r.freq || 0), 0);
    const percentage =
      totalTails > 0 ? ((mappedTails / totalTails) * 100).toFixed(1) : "0.0";

    return { totalTails, mappedTails, percentage };
  }, [
    rows,
    query,
    showOnlyUnmatched,
    selectedCountry,
    selectedManufacturer,
    approvedMappings,
    blacklistedRows,
  ]);

  // Blacklist handler - optimistic UI update (no page reload)
  const handleBlacklist = async (row: any) => {
    const key = `${row.faa_manufacturer}|${row.faa_model}`;

    // Optimistic update - hide row immediately
    setBlacklistedRows((prev) => new Set(prev).add(key));

    try {
      await postgrest.from("faa_to_airplx").upsert(
        {
          faa_manufacturer: row.faa_manufacturer,
          faa_model: row.faa_model,
          blacklisted: true,
        },
        { onConflict: "faa_manufacturer,faa_model" }
      );

      snackbar.notify({
        text: `Blacklisted "${row.faa_manufacturer} ${row.faa_model}"`,
        severity: "warning",
      });
    } catch (error: any) {
      // Rollback on error - show row again
      setBlacklistedRows((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });

      console.error("Failed to blacklist:", error);
      snackbar.notify({
        text: "Failed to blacklist. Please try again.",
        severity: "error",
      });
    }
  };

  // Bulk apply handlers
  const handleBulkApplyRequest = (aircraft: Aircraft, rowKey: string) => {
    if (!query.trim()) return; // Only allow bulk apply when searching

    setBulkApplyAircraft(aircraft);
    setBulkApplyRowKey(rowKey);
  };

  const handleBulkApply = async () => {
    if (!bulkApplyAircraft || !airplxIdentity?.userid) return;

    setBulkApplying(true);
    try {
      const { data, error } = await postgrest.rpc("bulk_apply_faa_pattern", {
        p_search_query: query,
        p_aircraft_id: bulkApplyAircraft.id,
        p_user_id: airplxIdentity.userid,
      });

      if (error) throw error;

      const result = data as { applied_count: number; skipped_count: number };

      snackbar.notify({
        text: `Applied ${result.applied_count} mappings (${result.skipped_count} skipped)`,
        severity: "success",
      });

      // Refresh patterns list
      setPatternsRefreshKey((k) => k + 1);

      // Clear bulk apply state
      setBulkApplyAircraft(null);
      setBulkApplyRowKey(null);

      // Refresh rows to show updated mappings (preserves search/filter state)
      setRowsRefreshKey((k) => k + 1);
    } catch (error: any) {
      console.error("Bulk apply failed:", error);
      snackbar.notify({
        text: `Bulk apply failed: ${error.message}`,
        severity: "error",
      });
    } finally {
      setBulkApplying(false);
    }
  };

  const handleBulkApplyDismiss = () => {
    setBulkApplyAircraft(null);
    setBulkApplyRowKey(null);
  };

  const handleReApplyPattern = async (pattern: FAAMappingPattern) => {
    if (!airplxIdentity?.userid || !pattern.aircraft) return;

    try {
      const { data, error} = await postgrest.rpc("bulk_apply_faa_pattern", {
        p_search_query: pattern.search_query,
        p_aircraft_id: pattern.matched_aircraft_id,
        p_user_id: airplxIdentity.userid,
      });

      if (error) throw error;

      const result = data as { applied_count: number; skipped_count: number };

      snackbar.notify({
        text: `Re-applied pattern "${pattern.search_query}": ${result.applied_count} mappings (${result.skipped_count} skipped)`,
        severity: "success",
      });

      // Refresh patterns list
      setPatternsRefreshKey((k) => k + 1);

      // Refresh rows (preserves search/filter state)
      setRowsRefreshKey((k) => k + 1);
    } catch (error: any) {
      console.error("Re-apply pattern failed:", error);
      snackbar.notify({
        text: `Re-apply failed: ${error.message}`,
        severity: "error",
      });
    }
  };

  const handleDeletePattern = async (patternId: string) => {
    try {
      const { error } = await postgrest
        .from("faa_mapping_patterns")
        .delete()
        .eq("id", patternId);

      if (error) throw error;

      snackbar.notify({
        text: "Pattern deleted",
        severity: "success",
      });

      // Refresh patterns list
      setPatternsRefreshKey((k) => k + 1);
    } catch (error: any) {
      console.error("Delete pattern failed:", error);
      snackbar.notify({
        text: `Delete failed: ${error.message}`,
        severity: "error",
      });
    }
  };

  // Function to render Aircraft cell with suggestions
  const renderAircraftCell = (row: any) => {
    const key = `${row.faa_manufacturer}|${row.faa_model}`;

    // Check for existing mapping from database OR local approval state
    const currentMapping = row.aircraft_id
      ? ({
          make: row.make,
          model: row.model,
          id: row.aircraft_id,
          linked_to: "", // Not included in view
          geom: null, // Not included in view (not needed for mapping)
        } as Aircraft)
      : approvedMappings.get(key) || null;

    const suggestion = suggestions.get(key) || null;

    const handleApprove = async (aircraft: Aircraft) => {
      if (!aircraft || !aircraft.id) {
        return;
      }

      // Update database
      await postgrest
        .from("faa_to_airplx")
        .update({
          matched_aircraft_id: aircraft.id,
        })
        .eq("faa_model", row.faa_model)
        .eq("faa_manufacturer", row.faa_manufacturer);

      // Update local state - this makes the checkmark disappear immediately
      setApprovedMappings((prev) => {
        const next = new Map(prev);
        next.set(key, aircraft);
        return next;
      });
    };

    const handleManualChange = async (aircraft: Aircraft) => {
      if (!aircraft || !aircraft.id) {
        return;
      }

      // Update database
      await postgrest
        .from("faa_to_airplx")
        .update({
          matched_aircraft_id: aircraft.id,
        })
        .eq("faa_model", row.faa_model)
        .eq("faa_manufacturer", row.faa_manufacturer);

      // Update local state
      setApprovedMappings((prev) => {
        const next = new Map(prev);
        next.set(key, aircraft);
        return next;
      });
    };

    // Calculate bulk apply context if applicable
    const bulkApplyContext =
      bulkApplyAircraft && bulkApplyRowKey === key && query.trim()
        ? {
            aircraft: bulkApplyAircraft,
            matchCount: filteredRowsToDisplay.filter((r) => !r.aircraft_id && !approvedMappings.has(`${r.faa_manufacturer}|${r.faa_model}`)).length,
            onApply: handleBulkApply,
            onDismiss: handleBulkApplyDismiss,
          }
        : null;

    return (
      <SuggestedAircraftCell
        key={key}
        suggestion={suggestion}
        currentMapping={currentMapping}
        onApprove={handleApprove}
        onManualChange={handleManualChange}
        onBulkApplyRequest={(aircraft) => handleBulkApplyRequest(aircraft, key)}
        bulkApplyContext={bulkApplyContext}
      />
    );
  };

  // Extract unique manufacturers from rows for the filter dropdown
  // Must be called before early returns to follow Rules of Hooks
  const manufacturers = React.useMemo(() => {
    if (!rows) return [];

    const uniqueManufacturers = new Set<string>();
    rows.forEach((row) => {
      if (row.faa_manufacturer) {
        uniqueManufacturers.add(row.faa_manufacturer);
      }
    });

    return Array.from(uniqueManufacturers).sort();
  }, [rows]);

  // Only show full-page loading on initial load (when no data exists yet)
  if ((loading && !rows) || loadingAircraft) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <Loading />
      </Box>
    );
  }

  const countryOptions: CountryOption[] = [
    { country: "All Countries", prefix: "" },
    ...(countries || []),
  ];

  return (
    <Stack direction="column" spacing={2} width="100%">
      {/* Recent Patterns Panel */}
      <RecentPatternsPanel
        patterns={patterns}
        loading={loadingPatterns}
        onReApply={handleReApplyPattern}
        onDelete={handleDeletePattern}
      />

      {coverageStats && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            Coverage: {coverageStats.percentage}% (
            {coverageStats.mappedTails.toLocaleString()} /{" "}
            {coverageStats.totalTails.toLocaleString()})
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          label="Search by FAA Manufacturer or Model"
          onChange={(evt) => handleSearchChange(evt.target.value)}
          variant="outlined"
          sx={{ width: 400 }}
        />
        <Autocomplete
          size="small"
          options={["All Manufacturers", ...manufacturers]}
          value={selectedManufacturer}
          onChange={(_, value) =>
            setSelectedManufacturer(value || "All Manufacturers")
          }
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <TextField {...params} label="Manufacturer" />
          )}
          ListboxProps={{
            style: { maxHeight: "40vh" },
          }}
          sx={{ width: 250 }}
        />
        <Autocomplete
          size="small"
          options={countryOptions}
          value={selectedCountry}
          onChange={(_, value) => {
            if (value) {
              setSelectedCountry(value);
            }
          }}
          getOptionLabel={(option) =>
            option.country === "All Countries"
              ? "All"
              : `${getCountryFlag(option.country)} ${option.country.substring(
                  0,
                  15
                )}`
          }
          renderInput={(params) => <TextField {...params} label="Country" />}
          isOptionEqualToValue={(option, value) =>
            option.country === value.country
          }
          sx={{ width: 250 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyUnmatched}
              onChange={(e) => setShowOnlyUnmatched(e.target.checked)}
            />
          }
          label="Unmatched"
          sx={{ width: 140 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyWithSuggestions}
              onChange={(e) => setShowOnlyWithSuggestions(e.target.checked)}
            />
          }
          label="With Suggestions"
          sx={{ width: 180, whiteSpace: "nowrap" }}
        />
      </Box>
      {loading && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      <TableContainer sx={{ maxHeight: 750 }}>
        <Table
          size="small"
          stickyHeader
          aria-label="FAA aircraft mapping table"
        >
          <TableHead>
            <TableRow>
              <TableCell
                width={150}
                sx={{
                  backgroundColor: "background.paper",
                  zIndex: 100,
                  borderBottom: 2,
                  borderColor: "divider",
                }}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  Frequency
                  <Tooltip
                    title={
                      selectedCountry.country === "All Countries"
                        ? "Total number of registered tail numbers for this aircraft type"
                        : `Format: "countryCount (totalCount)" - Showing ${selectedCountry.country} registrations with worldwide total in parentheses. Sorted by ${selectedCountry.country} count.`
                    }
                    arrow
                  >
                    <IconButton size="small" sx={{ padding: 0 }}>
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{ fontSize: 16 }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell
                width={200}
                sx={{
                  backgroundColor: "background.paper",
                  zIndex: 100,
                  borderBottom: 2,
                  borderColor: "divider",
                }}
              >
                FAA Manufacturer
              </TableCell>
              <TableCell
                width={200}
                sx={{
                  backgroundColor: "background.paper",
                  zIndex: 100,
                  borderBottom: 2,
                  borderColor: "divider",
                }}
              >
                FAA Model
              </TableCell>
              <TableCell
                width={300}
                sx={{
                  backgroundColor: "background.paper",
                  zIndex: 100,
                  borderBottom: 2,
                  borderColor: "divider",
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  AirPlx Aircraft
                  {suggestionCount > 0 && (
                    <Badge
                      badgeContent={suggestionCount}
                      color="success"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          height: "18px",
                          minWidth: "18px",
                        },
                      }}
                    >
                      <Tooltip
                        title={
                          <Box sx={{ p: 1, minWidth: 240 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, mb: 1 }}
                            >
                              Intelligent Suggestions ({suggestionCount})
                            </Typography>
                            <Divider sx={{ mb: 1 }} />

                            {confidenceBreakdown.high > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 0.25,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor: "success.main",
                                      mr: 1,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ flex: 1 }}>
                                    High Confidence
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {confidenceBreakdown.high}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    pl: 2.5,
                                    display: "block",
                                  }}
                                >
                                  CSV verified exact matches
                                </Typography>
                              </Box>
                            )}

                            {confidenceBreakdown.medium > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 0.25,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor: "warning.main",
                                      mr: 1,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ flex: 1 }}>
                                    Medium Confidence
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {confidenceBreakdown.medium}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    pl: 2.5,
                                    display: "block",
                                  }}
                                >
                                  Fuzzy matches - review carefully
                                </Typography>
                              </Box>
                            )}

                            <Divider sx={{ my: 1 }} />

                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontStyle: "italic",
                              }}
                            >
                              Click checkmarks to review and apply
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="bottom"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "background.paper",
                              color: "text.primary",
                              border: 1,
                              borderColor: "divider",
                              boxShadow: 3,
                            },
                          },
                        }}
                      >
                        <IconButton size="small" sx={{ padding: 0 }}>
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{ fontSize: 16 }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Badge>
                  )}
                </Box>
              </TableCell>
              <TableCell
                align="center"
                width={60}
                sx={{
                  backgroundColor: "background.paper",
                  zIndex: 100,
                  borderBottom: 2,
                  borderColor: "divider",
                }}
              >
                {/* Empty header - Ignore button column */}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRowsToDisplay.map((row) => (
              <TableRow key={row.faa_model + row.faa_manufacturer}>
                <TableCell>{formatFrequency(row, selectedCountry)}</TableCell>
                <TableCell>{row.faa_manufacturer}</TableCell>
                <TableCell>{row.faa_model}</TableCell>
                <TableCell>{renderAircraftCell(row)}</TableCell>
                <TableCell align="center">
                  {!row.aircraft_id && (
                    <Tooltip
                      title="Blacklist this FAA type (drones, balloons, etc.)"
                      placement="left"
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleBlacklist(row)}
                        sx={{
                          opacity: 0.5,
                          "&:hover": { opacity: 1, color: "error.main" },
                        }}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
